import React, { useEffect, useState } from 'react';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from './ui/select';
import {
    Calendar as CalendarIcon,
    Search,
    Users,
    Phone,
    Mail,
    Home,
    DollarSign,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import {
    format,
    isSameDay,
    parseISO,
    eachDayOfInterval,
    addMonths,
    subMonths
} from 'date-fns';

import { booking } from '@/lib/apiconfig';
import { getApi } from '@/lib/apiHelper';
import { useNavigate } from 'react-router-dom';

export const publicUrl = process.env.PUBLIC_URL;

const CalendarBooking = (props) => {
    const navigate = useNavigate();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const [bookingData, setBookingData] = useState([]);
    const [filteredBookings, setFilteredBookings] = useState([]);
    const [loading, setLoading] = useState(false);

    /* =========================
       FETCH BOOKINGS
    ========================= */
    useEffect(() => {
        setLoading(true);
        getApi(booking)
            .then((res) => {
                const data = res?.data?.bookings || [];
                setBookingData(data);
                setFilteredBookings(data);
            })
            .catch((err) => {
                console.error(err);
                setBookingData([]);
                setFilteredBookings([]);
            })
            .finally(() => setLoading(false));
    }, []);

    /* =========================
       APPLY FILTERS
    ========================= */
    useEffect(() => {
        let data = [...bookingData];

        if (searchQuery) {
            data = data.filter((b) =>
                b.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                b.roomType?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        if (statusFilter !== 'all') {
            data = data.filter((b) => b.status === statusFilter);
        }

        setFilteredBookings(data);
    }, [searchQuery, statusFilter, bookingData]);

    const getBookingsForDate = (date) => {
        return filteredBookings.filter((b) => {
            const checkIn = parseISO(b.checkIn);
            const checkOut = parseISO(b.checkOut);

            return eachDayOfInterval({
                start: checkIn,
                end: checkOut
            }).some((d) => isSameDay(d, date));
        });
    };

    const bookingsForSelectedDate = getBookingsForDate(selectedDate);

    const getStatusColor = (status) => {
        switch (status) {
            case 'confirmed':
                return 'bg-emerald-500/10 text-emerald-700 border-emerald-200';
            case 'pending':
                return 'bg-amber-500/10 text-amber-700 border-amber-200';
            case 'checked-in':
                return 'bg-blue-500/10 text-blue-700 border-blue-200';
            case 'cancelled':
                return 'bg-rose-500/10 text-rose-700 border-rose-200';
            default:
                return 'bg-gray-500/10 text-gray-700 border-gray-200';
        }
    };

    const totalBookings = filteredBookings.length;
    const confirmedBookings = filteredBookings.filter(b => b.status === 'confirmed').length;
    const pendingBookings = filteredBookings.filter(b => b.status === 'pending').length;
    const cancelledBookings = filteredBookings.filter(b => b.status === 'cancelled').length;
    const checkedinBookings = filteredBookings.filter(b => b.status === 'checked-in').length;
    const checkedoutBookings = filteredBookings.filter(b => b.status === 'checked-out').length;

    return (
        <div className="min-h-screen bg-slate-50">
            <div className="max-w-8xl mx-auto space-y-6">
                <div className="bg-white shadow-sm px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <h2 className="text-xl font-bold text-red-700">Luxtansa Admin</h2>
                        <span className="text-slate-500">Welcome, admin</span>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={() => navigate('https://luxtansaresorts.com/')}>View Site</Button>
                        <Button variant="destructive" onClick={() => navigate('/admin')}>Logout</Button>
                    </div>
                </div>
          
                <div className='flex items-center flex-col mx-6 my-4 gap-4'>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 w-80">
                        <Stat title="Total Bookings" icon={<img src={`${publicUrl + "/static/images/calendarw.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(59 116 246)" }} />} value={totalBookings} />
                        <Stat title="Confirmed" icon={<img src={`${publicUrl + "/static/images/confirmed.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(15 80 228)" }} />} value={confirmedBookings} />
                        <Stat title="Pending" icon={<img src={`${publicUrl + "/static/images/pending.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(245 158 11)" }} />} value={pendingBookings} />
                        <Stat title="Checked In" icon={<img src={`${publicUrl + "/static/images/checkedin.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(16 185 129)" }} />} value={checkedinBookings} />
                        <Stat title="Checked Out" icon={<img src={`${publicUrl + "/static/images/checkedout.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(107 114 128)" }} />} value={checkedoutBookings} />
                        <Stat title="Cancelled" icon={<img src={`${publicUrl + "/static/images/cancelled.png"}`} width={"40px"} className='rounded p-2' style={{ backgroundColor: "rgb(244 63 94)" }} />} value={cancelledBookings} />
                    </div>

                    <div className='w-80'>
                        <Card>
                            <CardHeader>
                                <CardTitle>Room Inventory</CardTitle>
                            </CardHeader>
                            <CardContent className="flex gap-4">
                                <Badge className="bg-yellow-100 text-yellow-800 px-4 py-2">4 Luxury Rooms</Badge>
                                <Badge className="bg-blue-100 text-blue-800 px-4 py-2">5 Premium Rooms</Badge>
                            </CardContent>
                        </Card>
                    </div>

                    <div className='w-80'>
                        <Card>
                        <CardHeader>
                            <CardTitle>Status Legend</CardTitle>
                        </CardHeader>
                        <CardContent className="flex gap-6 flex-wrap">
                            <Legend color="bg-blue-500" label="Confirmed" />
                            <Legend color="bg-amber-500" label="Pending" />
                            <Legend color="bg-emerald-500" label="Checked In" />
                            <Legend color="bg-gray-500" label="Checked Out" />
                            <Legend color="bg-rose-500" label="Cancelled" />
                        </CardContent>
                        </Card>
                    </div>

                    <Card className="w-80">
                        <CardContent className="p-4 flex gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                                <Input className="pl-10" placeholder="Search guest or room" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                            </div>

                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-48">
                                    <SelectValue placeholder="Status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All</SelectItem>
                                    <SelectItem value="confirmed">Confirmed</SelectItem>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="checked-in">Checked In</SelectItem>
                                    <SelectItem value="checked-out">Checked Out</SelectItem>
                                    <SelectItem value="cancelled">Cancelled</SelectItem>
                                </SelectContent>
                            </Select>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-80 flex flex-col">
                        <Card className="lg:col-span-2">
                            <CardHeader className="flex justify-between items-center">
                                <CardTitle>Calendar</CardTitle>
                                {/* <div className="flex items-center gap-2">
                                    <Button size="icon" variant="outline" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
                                        <ChevronLeft />
                                    </Button>
                                    <span>{format(currentMonth, 'MMMM yyyy')}</span>
                                    <Button size="icon" variant="outline" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
                                        <ChevronRight />
                                    </Button>
                                </div> */}
                            </CardHeader>
                            <CardContent>
                                <Calendar
                                    mode="single"
                                    selected={selectedDate}
                                    onSelect={(date) => {
                                        if (date) setSelectedDate(date);
                                    }}
                                    month={currentMonth}
                                    onMonthChange={setCurrentMonth}
                                    modifiers={{
                                        hasBookings: (date) => getBookingsForDate(date).length > 0
                                    }}
                                    modifiersClassNames={{
                                        hasBookings: 'bg-red-500 text-white rounded-full'
                                    }}
                                />
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>{format(selectedDate, 'MMM dd, yyyy')}</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3 max-h-96 overflow-auto">
                                {loading && <p className="text-center">Loading...</p>}

                                {!loading && bookingsForSelectedDate.length === 0 && (
                                    <p className="text-center text-slate-500">No bookings</p>
                                )}

                                {bookingsForSelectedDate.map((b) => (
                                    <div
                                        key={b.id}
                                        className="p-3 border rounded cursor-pointer hover:bg-slate-50"
                                        onClick={() => {
                                            setSelectedBooking(b);
                                            setIsDialogOpen(true);
                                        }}
                                    >
                                        <div className="flex justify-between">
                                            <div>
                                                <p className="font-semibold">Name: {b.name}</p>
                                                <p className="text-sm text-slate-500">Room type: {b.roomType}</p>
                                            </div>
                                            <Badge className={getStatusColor(b.status)}>
                                                {b.status}
                                            </Badge>
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>

                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                        <DialogContent className="max-w-2xl">
                            {selectedBooking && (
                                <>
                                    <DialogHeader>
                                        <DialogTitle className="text-2xl font-bold text-slate-800">
                                            Booking Details
                                        </DialogTitle>

                                        <DialogDescription>
                                            Complete information for booking #{selectedBooking.bookingId}
                                        </DialogDescription>
                                    </DialogHeader>

                                    <div className="space-y-6 mt-4">
                                        <div className="flex items-center justify-between">
                                            <Badge className={`${getStatusColor(selectedBooking.status)} border text-sm px-3 py-1`}>
                                                {selectedBooking.status.toUpperCase()}
                                            </Badge>
                                            <p className="text-sm text-slate-600">Booking ID: {selectedBooking.bookingId}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <Users className="w-4 h-4" />
                                                    <p className="text-sm font-medium">Guest Information</p>
                                                </div>

                                                <p className="text-lg font-semibold text-slate-800">{selectedBooking.name}</p>

                                                <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                                                    <Mail className="w-4 h-4" />
                                                    <p>{selectedBooking.email}</p>
                                                </div>

                                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                                    <Phone className="w-4 h-4" />
                                                    <p>{selectedBooking.phone}</p>
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <Home className="w-4 h-4" />
                                                    <p className="text-sm font-medium">Room Information</p>
                                                </div>

                                                <p className="text-lg font-semibold text-slate-800">{selectedBooking.roomType}</p>
                                                {/* <p className="text-sm text-slate-600">{selectedBooking.roomType}</p> */}
                                                <p className="text-sm text-slate-600 mt-2">
                                                    {selectedBooking.guests} {selectedBooking.guests === 1 ? 'Guest' : 'Guests'}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <CalendarIcon className="w-4 h-4" />
                                                    <p className="text-sm font-medium">Check-in</p>
                                                </div>
                                                <p className="text-lg font-semibold text-slate-800">
                                                    {format(parseISO(selectedBooking.checkIn), 'MMM dd, yyyy')}
                                                </p>
                                            </div>

                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <CalendarIcon className="w-4 h-4" />
                                                    <p className="text-sm font-medium">Check-out</p>
                                                </div>

                                                <p className="text-lg font-semibold text-slate-800">
                                                    {format(parseISO(selectedBooking.checkOut), 'MMM dd, yyyy')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* <div className="space-y-1">
                                            <div className="flex items-center gap-2 text-slate-600">
                                                <DollarSign className="w-4 h-4" />
                                                <p className="text-sm font-medium">Total Amount</p>
                                            </div>
                                            <p className="text-2xl font-bold text-slate-800">${selectedBooking.totalAmount}</p>
                                        </div> */}

                                        {selectedBooking.specialRequests && selectedBooking.specialRequests !== 'None' && (
                                            <div className="space-y-1">
                                                <p className="text-sm font-medium text-slate-600">Special Requests</p>
                                                <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-lg">
                                                    {selectedBooking.specialRequests}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

const Stat = ({ title, icon, value }) => (
    <Card>
        <div className='flex'>
            <div className='flex items-center p-3'>{icon}</div>
            <CardContent className="p-4">
                <p className="text-sm text-slate-500">{title}</p>
                <p className="text-2xl font-bold">{value}</p>
            </CardContent>
        </div>
    </Card>
);

const Legend = ({ color, label }) => (
    <div className="flex items-center gap-2">
        <span className={`w-3 h-3 rounded ${color}`} />
        <span className="text-sm">{label}</span>
    </div>
);

export default CalendarBooking;
